import { useEffect } from "react";
import { clerkFetch } from "../api/clerkFetch";

// Keeps the Bun host supplied with a fresh Clerk "convex"-template token.
//
// The host writes to Convex on the user's behalf long after the request that
// started the work: terminal snapshots, close records, cli-idle notifications,
// audit events and chat replies. Those tokens live about a minute, and the
// packaged desktop host holds no Clerk secret to mint new ones, so the only
// fresh tokens it ever gets are the ones this client sends (clerkFetch attaches
// X-Convex-Token to every request). While the user is just watching an agent
// work, nothing else is sent, so this beat sends one on a short interval and
// whenever the window comes back into view. See src/convexTokenStore.js.
export const HEARTBEAT_INTERVAL_MS = 15_000;
// A heartbeat request with no answer by then is aborted, so the next can go.
export const HEARTBEAT_TIMEOUT_MS = 10_000;
// A beat stuck that long before it could even send is given up on.
export const HEARTBEAT_ABANDON_MS = 60_000;
// Beats not yet finished, given-up ones included: past this, none starts.
export const HEARTBEAT_MAX_OUTSTANDING = 2;

const HEARTBEAT_PATH = "/api/auth/convex-token";

function hasClerkSession(): boolean {
  const clerk = (window as unknown as { Clerk?: { session?: unknown } }).Clerk;
  return Boolean(clerk && clerk.session);
}

// Whether `res` says no Steerly host serves this origin, so the beat should
// stop for good. The hosted web app (app.steerly.ai) is an assets-only Worker:
// it answers this POST with an empty 405, a plain static server 404s it, and an
// SPA fallback hands back its page with a 200. Anything else keeps the beat
// going: the host's own answer, or a passing failure such as a 5xx from a dev
// proxy whose host is still starting (a host that is restarting rejects the
// fetch, which the caller treats the same way).
async function noHostHere(res: Response): Promise<boolean> {
  if (res.status === 404 || res.status === 405) return true;
  if (!res.ok) return false;
  let body: { held?: unknown } | null;
  try {
    body = await res.json();
  } catch (err) {
    // A body that isn't JSON came from something other than the host. One that
    // never arrived (the beat timed out, the connection dropped) says nothing
    // about who answered: rethrow, so the next beat tries again.
    if (err instanceof SyntaxError) return true;
    throw err;
  }
  return typeof body?.held !== "boolean";
}

export function useHostConvexTokenHeartbeat(): void {
  useEffect(() => {
    let stopped = false;
    // The beat in flight. One at a time: a host that stops answering must not
    // collect a new request every interval. A request with no answer is
    // aborted after its timeout, which ends the beat so the next can go. A beat
    // still waiting on Clerk for its tokens (clerkFetch gets them before it
    // sends, and that can't be aborted) stays the one in flight, since a second
    // would most likely wait on Clerk too, but only for HEARTBEAT_ABANDON_MS:
    // then it is given up on, so one stuck call can't end the beat for good. A
    // given-up beat still runs (it can't be cancelled), so it counts toward
    // HEARTBEAT_MAX_OUTSTANDING until it finishes: stuck beats can't pile up.
    let inFlight: { ctrl: AbortController; startedAt: number } | null = null;
    let outstanding = 0;
    // Signed out: skip the beat rather than let clerkFetch's 401 handling open
    // the sign-in modal from a background timer.
    const beat = async () => {
      if (stopped || !hasClerkSession()) return;
      if (inFlight && Date.now() - inFlight.startedAt < HEARTBEAT_ABANDON_MS) return;
      if (outstanding >= HEARTBEAT_MAX_OUTSTANDING) return;
      const ctrl = new AbortController();
      const mine = { ctrl, startedAt: Date.now() };
      inFlight = mine;
      outstanding += 1;
      const giveUp = setTimeout(() => ctrl.abort(), HEARTBEAT_TIMEOUT_MS);
      try {
        const res = await clerkFetch(HEARTBEAT_PATH, { method: "POST", signal: ctrl.signal });
        if (await noHostHere(res)) stop();
      } catch {
        /* host briefly unreachable (restarting) or no answer: the next beat retries */
      } finally {
        clearTimeout(giveUp);
        outstanding -= 1;
        if (inFlight === mine) inFlight = null;
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") void beat();
    };
    const timer = setInterval(() => void beat(), HEARTBEAT_INTERVAL_MS);
    document.addEventListener("visibilitychange", onVisibility);
    function stop() {
      stopped = true;
      inFlight?.ctrl.abort();
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    }
    void beat();
    return stop;
  }, []);
}
